import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS = {
  en_attente: ["⏳ En attente de réponse", "text-amber-300 border-amber-500/40"],
  rappel_prepare: ["📨 Rappel préparé (Boîte d'envoi)", "text-sky-300 border-sky-500/40"],
  repondu: ["✅ A répondu", "text-emerald-300 border-emerald-500/40"],
  sans_suite: ["⛔ Sans suite", "text-zinc-400 border-zinc-700"],
};

/** Suivi des réponses des dirigeants Amikobi + rappel automatique J+7 sans réponse. */
export const AmikobiLeaderFollowups = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/amikobi/pilot/leader-followups"); setD(data); } catch { /* silencieux */ }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const patch = async (id, body, msg) => {
    setBusy(true);
    try { await api.patch(`/amikobi/pilot/leader-followups/${id}`, body); toast.success(msg); await load(); } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  const remind = async (id) => {
    if (!window.confirm("Préparer le rappel maintenant dans la Boîte d'envoi (À approuver) ?")) return;
    setBusy(true);
    try { await api.post(`/amikobi/pilot/leader-followups/${id}/remind-now`); toast.success("Rappel préparé — à approuver dans la Boîte d'envoi."); await load(); } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  if (!d || d.items.length === 0) return null;
  return (
    <div className="mt-3 rounded-xl border border-sky-500/40 bg-sky-500/5 p-3 text-xs space-y-2" data-testid="amikobi-leader-followups">
      <p className="text-sky-200 font-semibold">📬 Suivi des réponses des dirigeants — rappel automatique après {d.followup_days} jours sans réponse (préparé dans la Boîte d'envoi, tu approuves)</p>
      <div className="space-y-1.5">
        {d.items.map((f) => {
          const [label, cls] = STATUS[f.status] || STATUS.en_attente;
          return (
            <div key={f.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-black/40 px-3 py-2" data-testid={`leader-followup-${f.id}`}>
              <div className="min-w-[220px]">
                <div className="text-zinc-100 font-medium">{f.name || f.email}</div>
                <div className="text-zinc-500">{f.email} · envoyé le {String(f.sent_at).slice(0, 10)}{f.days_since != null && ` · J+${f.days_since}`}</div>
              </div>
              <span className={`px-2 py-0.5 rounded border ${cls}`} data-testid={`leader-status-${f.id}`}>{label}</span>
              {f.status === "en_attente" && f.reminder_due_in != null && <span className="text-zinc-500">rappel auto dans {f.reminder_due_in} j</span>}
              <div className="ml-auto flex gap-1.5">
                {f.status !== "repondu" && <button disabled={busy} onClick={() => patch(f.id, { status: "repondu" }, "Réponse notée ✓")} className="px-2 py-1 rounded border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/10" data-testid={`leader-replied-${f.id}`}>✅ A répondu</button>}
                {f.status !== "repondu" && <button disabled={busy} onClick={() => remind(f.id)} className="px-2 py-1 rounded border border-sky-500/50 text-sky-200 hover:bg-sky-500/10" data-testid={`leader-remind-${f.id}`}>📨 Rappel maintenant</button>}
                {f.status !== "sans_suite" && f.status !== "repondu" && <button disabled={busy} onClick={() => patch(f.id, { status: "sans_suite" }, "Marqué sans suite")} className="px-2 py-1 rounded border border-zinc-700 text-zinc-400 hover:bg-zinc-800" data-testid={`leader-nofollow-${f.id}`}>Sans suite</button>}
                {f.status !== "en_attente" && <button disabled={busy} onClick={() => patch(f.id, { status: "en_attente" }, "Remis en attente")} className="px-2 py-1 rounded border border-zinc-700 text-zinc-400 hover:bg-zinc-800" data-testid={`leader-reset-${f.id}`}>↺</button>}
              </div>
              <input defaultValue={f.note || ""} onBlur={(e) => e.target.value !== (f.note || "") && patch(f.id, { note: e.target.value }, "Note enregistrée")} placeholder="Note (ex. rencontre le 15 oct.)" className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-zinc-200" data-testid={`leader-note-${f.id}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
